"use client";
import React, { useState, useEffect, useRef, Suspense } from "react";
import Link from "next/link";
import Image from "next/image";
import NavSearch from "./NavSearch";
import { getEventsHref, getMerchantHref, getPromotionHref } from "@/constants/hooks";
import { Merchant } from "@/services/dataTypes";
import { faChevronDown, faArrowRight, faTimes, FontAwesomeIcon } from "@/constants/icons";

interface Props {
    categories: any[];
    merchantData: any[];
    events: any[];
    promotions: any[];
    cat_slug: string;
    mer_slug: string;
    mer_slug_type: string;
    promo_slug: string;
    blog_url?: string;
    blog_title?: string;
    companyLogo: string;
    headerPromoMerchant: Merchant[] | null;
    companyId: string;
    companyDomain: string;
}

const MobileNavMenu = ({
    categories, merchantData, events, promotions,
    cat_slug, mer_slug, mer_slug_type, promo_slug,
    blog_url, blog_title, companyLogo,
    companyId, companyDomain, headerPromoMerchant,
}: Props) => {
    const [open,      setOpen]      = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const drawerRef                 = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent | TouchEvent) => {
            if (drawerRef.current && !drawerRef.current.contains(e.target as Node))
                setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
        };
    }, [open]);

    const toggle = (tab: string) => setActiveTab(p => p === tab ? null : tab);
    const close  = () => { setOpen(false); setActiveTab(null); };

    const navGroups = [
        {
            name: "Categories", tab: "categories", path: `/${cat_slug}`,
            items: categories?.slice(0, 12),
            getHref:  (i: any) => `/${i?.url}`,
            getLabel: (i: any) => i?.name,
        },
        {
            name: "Stores", tab: "stores", path: "/all-stores/A",
            items: merchantData?.slice(0, 12),
            getHref:  (i: any) => getMerchantHref(i, mer_slug, mer_slug_type),
            getLabel: (i: any) => i?.merchant_name,
        },
        {
            name: "Products", tab: "products", path: "/all-products",
            items: [
                { id: "p1", name: "Brand Products", href: "/products"     },
                { id: "p2", name: "All Products",   href: "/all-products" },
            ],
            getHref:  (i: any) => i?.href,
            getLabel: (i: any) => i?.name,
        },
        ...(events?.length > 0 ? [{
            name: "Events", tab: "events", path: "/events",
            items: events,
            getHref:  (i: any) => getEventsHref(i, mer_slug_type),
            getLabel: (i: any) => i?.name,
        }] : []),
        ...(promotions?.length > 0 ? [{
            name: "Promotions", tab: "promotions", path: `/${promo_slug}`,
            items: promotions,
            getHref:  (i: any) => getPromotionHref(i, promo_slug),
            getLabel: (i: any) => i?.name,
        }] : []),
    ];

    return (
        <div className="lg:hidden">

            {/* ── Hamburger ── */}
            <button
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                className="flex items-center justify-center w-10 h-10 rounded-xl
                           border border-slate-200 bg-white text-slate-600
                           hover:border-[#D61C4E]/30 hover:text-[#D61C4E]
                           active:scale-95 transition-all shadow-sm"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                        d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>

            {/* ── Backdrop ── */}
            <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]
                             transition-opacity duration-300
                             ${open ? "opacity-100 visible" : "opacity-0 invisible"}`}
                onClick={close} />

            {/* ── Drawer ── */}
            <div
                ref={drawerRef}
                className={`fixed top-0 right-0 h-screen h-[100dvh] w-[85%] max-w-[390px]
                            flex flex-col z-[9999] shadow-2xl
                            transition-transform duration-400 ease-in-out
                            ${open ? "translate-x-0" : "translate-x-full"}`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4
                                bg-[#0F172A] border-b border-slate-800 shrink-0">
                    <Link href="/" onClick={close}>
                        <Image src={companyLogo} alt="Logo" width={130} height={40}
                            className="h-8 w-auto object-contain brightness-0 invert" priority />
                    </Link>
                    <button onClick={close} aria-label="Close"
                        className="w-9 h-9 flex items-center justify-center rounded-xl
                                   bg-[#D61C4E] text-white active:scale-90 transition-all
                                   shadow-md shadow-[#D61C4E]/25">
                        <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-4 py-3 bg-white border-b border-slate-100 shrink-0">
                    <Suspense fallback={<div className="h-10 bg-slate-50 rounded-full animate-pulse" />}>
                        <NavSearch companyId={companyId} mer_slug={mer_slug}
                            slug_type={mer_slug_type} cat_slug={cat_slug} domain={companyDomain} />
                    </Suspense>
                </div>

                {/* Promo merchants */}
                {(headerPromoMerchant?.length ?? 0) > 0 && (
                    <div className="px-4 py-2.5 bg-white border-b border-slate-100
                                    flex flex-wrap gap-2 shrink-0">
                        {headerPromoMerchant!.slice(0, 3).map((item, i) => (
                            <Link key={i} href={getMerchantHref(item, mer_slug, mer_slug_type)}
                                onClick={close}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                           bg-[#D61C4E]/5 border border-[#D61C4E]/15
                                           text-[9px] font-black text-[#D61C4E] uppercase tracking-wider
                                           no-underline hover:bg-[#D61C4E] hover:text-white transition-all">
                                <span className="w-1 h-1 rounded-full bg-[#D61C4E]" />
                                {item?.promotional_tag || item?.merchant_name}
                            </Link>
                        ))}
                    </div>
                )}

                {/* Nav links — scrollable */}
                <div className="flex-1 overflow-y-auto bg-slate-50
                                [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    <div className="p-3 space-y-1.5">

                        {/* Home */}
                        <Link href="/" onClick={close}
                            className="flex items-center px-4 py-3.5 rounded-xl
                                       bg-white border border-slate-100
                                       text-[13px] font-bold text-slate-700
                                       hover:border-[#D61C4E]/20 hover:text-[#D61C4E]
                                       transition-all no-underline">
                            Home
                        </Link>

                        {/* Accordion nav groups */}
                        {navGroups.map((group) => (
                            <div key={group.tab}
                                className="bg-white border border-slate-100 rounded-xl overflow-hidden">

                                {/* Toggle button */}
                                <button onClick={() => toggle(group.tab)}
                                    className={`w-full flex items-center justify-between gap-3
                                                px-4 py-3.5 text-[13px] font-bold transition-colors
                                                ${activeTab === group.tab ? "text-[#D61C4E]" : "text-slate-700"}`}>
                                    {group.name}
                                    <FontAwesomeIcon icon={faChevronDown}
                                        className={`w-3 h-3 shrink-0 transition-transform duration-300
                                                    ${activeTab === group.tab
                                                        ? "rotate-180 text-[#D61C4E]"
                                                        : "text-slate-300"}`} />
                                </button>

                                {/* Accordion panel */}
                                <div style={{
                                    maxHeight: activeTab === group.tab ? "600px" : "0",
                                    overflow: "hidden",
                                    transition: "max-height 0.35s ease-in-out",
                                }}>
                                    <div className="px-3 pb-3 border-t border-slate-50">
                                        <div className="grid grid-cols-2 gap-1.5 pt-3">
                                            {group.items?.map((item: any, i: number) => (
                                                <Link key={i} href={group.getHref(item)} onClick={close}
                                                    className="px-3 py-2.5 rounded-lg text-center
                                                               text-[11px] font-semibold text-slate-600
                                                               bg-slate-50 border border-slate-100
                                                               hover:bg-[#D61C4E] hover:text-white
                                                               hover:border-[#D61C4E]
                                                               transition-all no-underline">
                                                    {group.getLabel(item)}
                                                </Link>
                                            ))}
                                        </div>

                                        {/* View All */}
                                        <Link href={group.path} onClick={close}
                                            className="mt-2 flex items-center justify-center gap-2 py-2.5
                                                       rounded-lg border border-[#D61C4E]/20 bg-[#D61C4E]/5
                                                       text-[10px] font-black uppercase tracking-[0.15em]
                                                       text-[#D61C4E] hover:bg-[#D61C4E] hover:text-white
                                                       transition-all no-underline group/va">
                                            View All {group.name}
                                            <FontAwesomeIcon icon={faArrowRight}
                                                className="w-2.5 h-2.5 group-hover/va:translate-x-0.5 transition-transform" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Blog */}
                        {blog_title && blog_url && (
                            <Link href={blog_url} onClick={close}
                                className="flex items-center px-4 py-3.5 rounded-xl
                                           bg-white border border-slate-100
                                           text-[13px] font-bold text-slate-700
                                           hover:border-[#D61C4E]/20 hover:text-[#D61C4E]
                                           transition-all no-underline">
                                {blog_title}
                            </Link>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 bg-white border-t border-slate-100 shrink-0">
                    <p className="text-center text-[9px] text-slate-300 font-black uppercase tracking-[0.25em]">
                        © {new Date().getFullYear()} All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MobileNavMenu;
